import {
  LAAG_NAAM,
  afkoppelStatus,
  maatregelenStatus,
  type KaartStatus,
  type Registratie,
} from '@/types/domain';

/**
 * De zinnen die onder een stip op de kaart verschijnen.
 *
 * Waarom dit een eigen bestand is. De gemeente las bij een huurwoning dit:
 *
 *     Gaat niet afkoppelen
 *     huurwoning — corporatie koppelt af
 *
 * en vroeg zich terecht af wat daar nu waar van was. Beide regels klopten,
 * maar geen van beide noemde waar hij over ging. De eerste over de bewoner
 * en zijn tuin, de tweede over de corporatie en het pand. Wie dat niet weet
 * plakt de zinnen aan elkaar en houdt een tegenspraak over.
 *
 * Twee regels met een kop ervoor lossen dat op. De koppen zijn precies de
 * twee lagen van de kaart, in precies dezelfde woorden als de schakelaar in
 * de legenda. En elke zin noemt zelf wie er handelt, zodat hij ook los van
 * zijn kop niet te misverstaan is.
 *
 * De woorden zijn gekozen op maar één lezing. "Koppelt niet af" gaat over de
 * woning en niet over de gezindheid van de bewoner; "overweegt" belooft
 * niets.
 */

/** Eén regel: waar hij over gaat, en wat er aan de hand is. */
export interface TooltipRegel {
  /** De laagnaam: 'Afkoppelen' of 'Extra maatregelen'. */
  wie: string;
  /** De volledige zin, met de handelende partij erin. */
  wat: string;
}

/**
 * De drie standen waarin er nog niets besloten is.
 *
 * Die lezen in beide lagen gelijk, want ze gaan niet over het besluit maar
 * over het gesprek dat er nog niet was.
 */
function nogNiets(status: KaartStatus): string | null {
  if (status === 'Niet thuis') return 'nog niet gesproken — was niet thuis';
  if (status === 'Nog geen keuze gemaakt') return 'gesproken, nog geen keuze gemaakt';
  if (status === 'Nog niet bezocht') return 'nog niet gesproken';
  return null;
}

/** Gaat de hemelwaterafvoer van het riool af, en van wie is dat besluit? */
function zinAfkoppelen(registratie: Registratie | undefined, huur: boolean): string {
  const status = afkoppelStatus(registratie, huur);

  if (huur) {
    return status === 'Afgekoppeld'
      ? 'de corporatie heeft dit pand afgekoppeld'
      : 'de corporatie koppelt dit pand af';
  }

  if (status === 'Afgekoppeld') return 'de eigenaar heeft het pand afgekoppeld';
  if (status === 'Wordt afgekoppeld') return 'staat vast, de subsidie is toegekend';
  if (status === 'Overweegt afkoppelen') return 'de eigenaar overweegt afkoppelen';
  if (status === 'Koppelt niet af') return 'de eigenaar koppelt niet af';

  return nogNiets(status) ?? 'nog niet gesproken';
}

/**
 * Wat de bewoner zelf op het perceel doet; bij koop en huur dezelfde vraag.
 *
 * Bij een koopwoning heet hij hier ook "de eigenaar", want dat is dezelfde
 * persoon. Twee namen voor één iemand, in twee regels onder elkaar, leest als
 * twee partijen — en dat is precies het misverstand dat we hier voorkomen.
 */
function zinMaatregelen(registratie: Registratie | undefined, huur: boolean): string {
  const status = maatregelenStatus(registratie);
  const wie = huur ? 'de bewoner' : 'de eigenaar';

  if (status === 'Overweegt afkoppelen') return `${wie} overweegt maatregelen`;
  if (status === 'Koppelt niet af') return `${wie} neemt geen maatregelen`;

  return nogNiets(status) ?? 'nog niet gesproken';
}

/**
 * De twee regels voor één woning.
 *
 * Altijd allebei, ook als ze hetzelfde zeggen. Een regel die soms wegvalt
 * laat de lezer raden of het antwoord ontbreekt of de vraag.
 */
export function tooltipRegels(
  registratie: Registratie | undefined,
  huur: boolean
): TooltipRegel[] {
  return [
    { wie: LAAG_NAAM.afkoppelen, wat: zinAfkoppelen(registratie, huur) },
    { wie: LAAG_NAAM.maatregelen, wat: zinMaatregelen(registratie, huur) },
  ];
}
